import { Subject, fromEvent } from 'rxjs';
import getArticlesSearchCommand from '../services/articleSearchParametersService';

const articleCardCarouselSettingsSubject = new Subject();

/**
 * Retrieves the query selector for a specific carousel element.
 *
 * This function extracts the `data-article-card-carousel` attribute from the provided element
 * and constructs a query selector string for it. If the attribute is not present, it returns `null`.
 *
 * @param {HTMLElement} carouselElement - The carousel element to extract the selector from.
 * @returns {string|null} The query selector string for the carousel, or `null` if the attribute is missing.
 *
 * @example
 * const carouselElement = document.querySelector('[data-article-card-carousel="carousel1"]');
 * const querySelector = getArticleCardCarouselQuerySelector(carouselElement);
 * console.log(querySelector); // '[data-article-card-carousel="carousel1"]'
 */
const getArticleCardCarouselQuerySelector = function (carouselElement) {
  const carouselIdentifier = carouselElement.getAttribute('data-article-card-carousel');
  return carouselIdentifier ? `[data-article-card-carousel="${carouselIdentifier}"]` : null;
};

/**
 * Loads the settings for all article card carousels on the page.
 *
 * This function selects all elements with the `data-article-card-carousel` attribute,
 * extracts their search parameters and query selectors, and emits the settings
 * for each carousel through the `articleCardCarouselSettingsSubject`.
 */
const loadArticleCardCarouselSettings = function() {
  const articleCardsCarouselElements = document.querySelectorAll('[data-article-card-carousel]');

  articleCardsCarouselElements.forEach((carouselSection) => {
    const articleSearchParametersElement = carouselSection.querySelector('[data-article-search-parameters]');
    
    const carouselSetting = {
      carouselQuerySelector: getArticleCardCarouselQuerySelector(carouselSection),
      articlesSearchCommand: getArticlesSearchCommand(articleSearchParametersElement),
    };

    // Emit the carousel settings
    articleCardCarouselSettingsSubject.next(carouselSetting);
  });
};

/**
 * Observable that emits when the document content is fully loaded.
 * THIS IS TO AVOID TRY TO READ THE DOM BEFORE IT IS FULLY LOADED
 * This observable listens for the `DOMContentLoaded` event and triggers the
 * `loadArticleCardCarouselSettings` function to initialize the carousel settings.
 */
const onDocumentContentLoaded$ = fromEvent(document, 'DOMContentLoaded');
onDocumentContentLoaded$.subscribe(loadArticleCardCarouselSettings);
const onArticleCardCarouselSettingsReady$ = articleCardCarouselSettingsSubject.asObservable();

export default onArticleCardCarouselSettingsReady$;